// Hook that owns the applied-filter-chip state for the Sankey search bar.
//
// Computes the matching node-id set whenever chips or row-slice changes,
// memoised so the SankeyChart's filter highlight prop is referentially
// stable when filters don't change.

import { useCallback, useMemo, useState } from 'react';
import type { SearchEntity } from './searchIndex';
import { applyFilters, type FilterResult } from './searchFilter';
import type { MPPRow } from './types';

function chipKey(e: SearchEntity): string {
  return `${e.kind}::${e.value}`;
}

export interface UseSankeyFilters {
  chips: SearchEntity[];
  add: (entity: SearchEntity) => void;
  remove: (entity: SearchEntity) => void;
  clear: () => void;
  result: FilterResult;
}

export function useSankeyFilters(rows: readonly MPPRow[]): UseSankeyFilters {
  const [chips, setChips] = useState<SearchEntity[]>([]);

  const add = useCallback((entity: SearchEntity) => {
    setChips((prev) => {
      const k = chipKey(entity);
      if (prev.some((c) => chipKey(c) === k)) return prev;
      return [...prev, entity];
    });
  }, []);

  const remove = useCallback((entity: SearchEntity) => {
    const k = chipKey(entity);
    setChips((prev) => prev.filter((c) => chipKey(c) !== k));
  }, []);

  const clear = useCallback(() => setChips([]), []);

  const result = useMemo(() => applyFilters(chips, rows), [chips, rows]);

  return { chips, add, remove, clear, result };
}
